<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.group_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: $tc('messages.teacher_actions', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex space-x-2 items-center">
            <NavLinks :links="meta.navLinks" />
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.status')" for="status" />
                        <FormSelect
                            required
                            id="gradable"
                            v-model="form.status"
                            :options="options.status"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.status" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-8">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <EmptyState picture v-if="!gamificationItems.data.length">
            <template v-slot:picture />
        </EmptyState>
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-1 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <SelectAllNoneButtons
                                        :max-count="gamificationItems.data.length"
                                        :selected-count="submitForm.gamification_score_card_item_ids.length"
                                        @select-all="selectAll()"
                                        @select-none="submitForm.gamification_score_card_item_ids = []"
                                        class="flex md:w-12"
                                    />
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.gamification_items', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.when') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(gamificationItem, index) in gamificationItems.data"
                                :key="gamificationItem.id"
                            >
                                <td
                                    class="flex justify-center px-1 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                >
                                    <input
                                        type="checkbox"
                                        :value="gamificationItem.id"
                                        v-model="submitForm.gamification_score_card_item_ids"
                                        name="gamificationItem"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-2 items-center">
                                        <img :src="gamificationItem.image_path" alt="" class="h-6" />
                                        <div>
                                            <div :title="gamificationItem.item_description">
                                                {{ gamificationItem.item_name }}
                                            </div>
                                            <div class="text-xs">
                                                {{ gamificationItem.type_display }}
                                                <BadgePill color="gray" v-if="gamificationItem.is_draft">
                                                    {{ $t('messages.draft') }}</BadgePill
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ gamificationItem.student_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ gamificationItem.created_at_diff_for_humans }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <inertia-link
                                        preserve-scroll
                                        v-if="filters.status !== 'used'"
                                        method="patch"
                                        as="button"
                                        :href="gamificationItem.links.update_confirm"
                                        :data="{
                                            gamification_score_card_item_ids: [gamificationItem.id],
                                        }"
                                        class="px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 inline-flex items-center border border-transparent font-medium rounded shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        {{ $t('messages.confirm') }}
                                    </inertia-link>
                                    <inertia-link
                                        preserve-scroll
                                        method="patch"
                                        as="button"
                                        :href="gamificationItem.links.update_return"
                                        :data="{
                                            gamification_score_card_item_ids: [gamificationItem.id],
                                        }"
                                        class="inline-flex items-center border border-gray-300 shadow-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-gray-700 bg-white hover:bg-gray-50 px-3 py-2 text-sm"
                                    >
                                        {{ $t('messages.return_item') }}
                                    </inertia-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="flex space-x-2 items-center mt-3" v-if="submitForm.gamification_score_card_item_ids.length">
            <form action="" @submit.prevent="submitConfirm()">
                <AppButton
                    desired-action
                    v-if="filters.status !== 'used'"
                    element="button"
                    as="button"
                    :disabled="submitForm.processing"
                >
                    {{ $t('messages.confirm') }}
                    <span v-if="submitForm.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </form>

            <form action="" @submit.prevent="submitReturn()">
                <AppButton element="button" as="button" :disabled="submitForm.processing">
                    {{ $t('messages.return_item') }}
                    <span v-if="submitForm.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </form>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="gamificationItems" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import BadgePill from '@/components/BadgePill.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import NavLinks from '@/components/NavLinks.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        FormLabel,
        FormSelect,
        FormError,
        BadgePill,
        AppButton,
        EmptyState,
        InertiaPagination,
        SelectAllNoneButtons,
        NavLinks,
    },
    layout: Layout,
    name: 'Show',
    props: ['filters', 'meta', 'options', 'group', 'gamificationItems'],
    data() {
        return {
            form: this.$inertia.form({
                status: this.filters.status,
            }),
            submitForm: this.$inertia.form({
                gamification_score_card_item_ids: [],
                status: '',
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        submit() {
            this.submitForm.patch(this.meta.links.update, {
                preserveScroll: true,
                onSuccess: () => {
                    this.submitForm.reset();
                },
            });
        },
        submitConfirm() {
            this.submitForm.status = 'used';
            this.submit();
        },
        submitReturn() {
            this.submitForm.status = 'new';
            this.submit();
        },
        selectAll() {
            this.submitForm.gamification_score_card_item_ids = this.gamificationItems.data.map((i) => i.id);
        },
    },
    computed: {},
};
</script>
